import React, { useState, useEffect, useMemo, useRef } from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import { useFlexSearch } from "react-use-flexsearch"
import { graphql, useStaticQuery } from 'gatsby'

import { IntegrationsAppDirectoryQueryQuery } from '@graphqlTypes'

import Badge, { BADGE_COLORS, BADGE_SIZES } from "@components/Badge/Badge"
import FilteringGrid from '@components/FilteringGrid/FilteringGrid.js'
import SearchInput from '@components/SearchInput/SearchInput'
import Tabs from '@components/Tabs/Tabs'

import {
  IntegrationsAppDirectoryCardLINK,
  IntegrationsAppDirectoryGridDIV,
  IntegrationsAppDirectorySearchDIV,
  IntegrationsAppDirectorySortDIV,
  IntegrationsAppDirectoryWrapperDIV,
  ZapierBadgeDIV,
} from './IntegrationsAppDirectory.styles'

import {
  INTEGRATIONS_APP_DIRECTORY_SORT_TYPES,
  INTEGRATIONS_APP_DIRECTORY_SORT_TYPES_LABELS,
} from './IntegrationsAppDirectory.constants'
import LpSection from '@components/lp/LpSection/LpSection'

const IntegrationsAppDirectory = () => {
  const {
    localSearchIntegrations,
    integrationsData: { nodes: integrations }
  } = useStaticQuery<IntegrationsAppDirectoryQueryQuery>(graphql`
    fragment IntegrationCardFragment on ContentfulIntegration {
      category
      id
      isFeatured
      isPlus
      link
      logo {
        imgixImage {
          gatsbyImageData(width: 130)
        }
      }
      popularity
      slug
      subtitle
      tags
      title
      usesZapier
    }
    query IntegrationsAppDirectoryQuery {
      localSearchIntegrations {
        index
        store
      }
      integrationsData: allContentfulIntegration(
          sort: {title: ASC}
      ) {
        nodes {
          ...IntegrationCardFragment
        }
      }
    }
  `)

  const getIntegrationsKeys = (_integrations: IntegrationsAppDirectoryQueryQuery["integrationsData"]["nodes"]) => _integrations.map(({ id }) => id)

  // Memoized sorted keys
  const sortedIntegrationKeys = useMemo(() => ({
    [INTEGRATIONS_APP_DIRECTORY_SORT_TYPES.POPULAR]:
      getIntegrationsKeys([
        ...integrations
          .filter(({ popularity }) => popularity)
          .sort((a, b) => {
            if (!b.popularity) return -1
            if (!a.popularity) return 1
            if (a.popularity < b.popularity) return 1
            return -1
          }),
        ...integrations.filter(({ popularity }) => !popularity)
      ]),
    [INTEGRATIONS_APP_DIRECTORY_SORT_TYPES.AZ]: getIntegrationsKeys(integrations),
  }), [integrations])

  // Memoized categories
  const categories = useMemo(() => (
    [
      ...new Set(
        integrations.map(({ category }) => category).flat()
      )
    ].filter(c => c).sort((a, b) => a.toUpperCase() > b.toUpperCase() ? 1 : -1)
  ), [integrations])

  // State variables
  const [activeSortType, setActiveSortType] = useState<INTEGRATIONS_APP_DIRECTORY_SORT_TYPES>(INTEGRATIONS_APP_DIRECTORY_SORT_TYPES.POPULAR)
  const [activeCategory, setActiveCategory] = useState<string | number | null>(null)
  const [filteredIntegrationKeys, setFilteredIntegrationKeys] = useState<string[]>(sortedIntegrationKeys[activeSortType])
  const [searchQuery, setSearchQuery] = useState<string>('')
  const searchResults = useFlexSearch(searchQuery, localSearchIntegrations?.index, localSearchIntegrations?.store)
  const debounceRef = useRef<NodeJS.Timeout>()

  // Filter integrations when the category changes (debounced using a ref)
  useEffect(() => {
    const refilterIntegrationsKeys = () => {
      const _filteredIntegrationsKeys = getIntegrationsKeys(
        // IF a search query is set but there are no results, return an empty array
        (searchQuery && !searchResults.length) ? [] :
          // IF an activeCategory is set and/or there are search results available, filter the integrations!
          (activeCategory || searchResults.length) ?
            integrations.filter(integration => {
              if (activeCategory && !integration.category?.includes(activeCategory)) return false
              if (searchResults.length && !searchResults.find(({ id }) => id === integration.id)) return false
              return true
            }) :
            // Otherwise return the default array
            integrations
      )

      setFilteredIntegrationKeys(_filteredIntegrationsKeys)
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(refilterIntegrationsKeys, 200)
  }, [activeCategory, searchQuery, searchResults, integrations])

  return (
    <LpSection title="App Directory">
      <div className="container">
        <IntegrationsAppDirectoryGridDIV>
          <div className="column">
            <IntegrationsAppDirectoryWrapperDIV>
              <div className="column column--2-12-M column--4-10-L">
                <IntegrationsAppDirectorySearchDIV>
                  <SearchInput
                    onClearQuery={() => setSearchQuery('')}
                    onSearch={value => setSearchQuery(value)}
                    query={searchQuery}
                  />
                </IntegrationsAppDirectorySearchDIV>
              </div>
              <IntegrationsAppDirectorySortDIV>
                <strong >Sort By:</strong>
                {Object.keys(INTEGRATIONS_APP_DIRECTORY_SORT_TYPES).reverse().map(sortType => (
                  <label
                    className="type--text-M"
                    htmlFor={`gridSort-${sortType}`}
                    key={sortType}
                  >
                    <input
                      checked={activeSortType === sortType}
                      id={`gridSort-${sortType}`}
                      name="gridSort"
                      onChange={() => setActiveSortType(sortType as INTEGRATIONS_APP_DIRECTORY_SORT_TYPES)}
                      type="radio"
                      value={sortType}
                    />
                    {INTEGRATIONS_APP_DIRECTORY_SORT_TYPES_LABELS[sortType as INTEGRATIONS_APP_DIRECTORY_SORT_TYPES]}
                  </label>
                ))}
              </IntegrationsAppDirectorySortDIV>
              <Tabs
                activeOption={activeCategory}
                onClickOption={newCategory => setActiveCategory(newCategory)}
                options={[
                  {
                    title: 'All',
                    value: null,
                  },
                  ...categories.map(category => ({
                    title: category || "",
                    value: category || "",
                  })),
                ]}
              />
              <FilteringGrid
                allCards={integrations.map((integration) => ({
                  key: integration.id,
                  render: (isActive: boolean) => (
                    <IntegrationsAppDirectoryCardLINK
                      href={integration.link || `/help-desk-integration/${integration.slug}`}
                      tabIndex={isActive ? 0 : -1}
                    >
                      {integration.isPlus && <Badge className="Badge" color={BADGE_COLORS.PURPLE} size={BADGE_SIZES.XS}>PLUS</Badge>}
                      {integration.usesZapier && <ZapierBadgeDIV title="Integrates using Zapier" />}
                      {integration.logo?.imgixImage?.gatsbyImageData && (
                        <GatsbyImage
                          alt={integration.title || "Integration App Logo"}
                          image={integration.logo.imgixImage.gatsbyImageData}
                        />
                      )}
                      <h5>{integration.title}</h5>
                    </IntegrationsAppDirectoryCardLINK>
                  )
                }))}
                filteredCardKeys={filteredIntegrationKeys}
                sortedCardKeys={sortedIntegrationKeys[activeSortType]}
              />
            </IntegrationsAppDirectoryWrapperDIV>
          </div>
        </IntegrationsAppDirectoryGridDIV>
      </div>
    </LpSection>
  )
}

export default IntegrationsAppDirectory
